// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CoverageEligibilityResponse } from '@medplum/fhirtypes';
import type { ExclusionCriterion } from '@polarissleep/shared';

// Keep these thresholds in sync with the HSAT triage algorithm in
// apps/provider-app/bots/services/hsat-triage/constants.ts.
const STOP_BANG_NOT_MEDICALLY_NECESSARY_THRESHOLD = 5;
const EPWORTH_NOT_MEDICALLY_NECESSARY_THRESHOLD = 8;

export const TRIAGE_ALERT_ARIA_LABELS = {
  eligibilitySignal: 'HSAT eligibility review alert',
  insuranceIneligible: 'Insurance ineligible alert',
  stopBangNotMedicallyNecessary: 'STOP-BANG not medically necessary alert',
  epworthNotMedicallyNecessary: 'Epworth not medically necessary alert',
  clinicalExclusion: 'Clinical exclusion criteria met alert',
} as const;

interface TriageAlertRuleInput {
  isSelfPay: boolean;
  coverageEligibilityResponse: CoverageEligibilityResponse | undefined;
  stopBangScore: number | undefined;
  epworthScore: number | undefined;
  stopBangComplete?: boolean;
  epworthComplete?: boolean;
  exclusions: ExclusionCriterion[];
}

export type EligibilitySignal = 'Flagged for Review' | 'Probably Eligible' | 'Incomplete Assessment';

export function getInsuranceEligibility(response: CoverageEligibilityResponse | undefined): string {
  const insurance = response?.insurance;
  if (!insurance || insurance.length === 0) {
    return '';
  }
  return insurance[0].inforce ? 'Eligible' : 'Ineligible';
}

export function shouldShowInsuranceIneligibleAlert(input: TriageAlertRuleInput): boolean {
  return !input.isSelfPay && getInsuranceEligibility(input.coverageEligibilityResponse) === 'Ineligible';
}

export function shouldShowStopBangNotMedicallyNecessaryAlert(input: TriageAlertRuleInput): boolean {
  return input.stopBangScore !== undefined && input.stopBangScore < STOP_BANG_NOT_MEDICALLY_NECESSARY_THRESHOLD;
}

export function shouldShowEpworthNotMedicallyNecessaryAlert(input: TriageAlertRuleInput): boolean {
  return input.epworthScore !== undefined && input.epworthScore < EPWORTH_NOT_MEDICALLY_NECESSARY_THRESHOLD;
}

export function shouldShowClinicalExclusionAlert(input: TriageAlertRuleInput): boolean {
  return input.exclusions.length > 0;
}

/**
 * Applies HSAT Algorithm rules to the values shown in the triage queue.
 * Epworth only affects the signal when STOP-BANG is below its threshold.
 */
export function getEligibilitySignal(input: TriageAlertRuleInput): EligibilitySignal {
  const epworthScore = input.epworthScore;
  if (shouldShowClinicalExclusionAlert(input)) {
    return 'Flagged for Review';
  }

  if (input.stopBangScore === undefined || input.stopBangComplete === false) {
    return 'Incomplete Assessment';
  }

  if (input.stopBangScore >= STOP_BANG_NOT_MEDICALLY_NECESSARY_THRESHOLD) {
    return 'Probably Eligible';
  }

  if (epworthScore === undefined || input.epworthComplete === false) {
    return 'Incomplete Assessment';
  }

  return epworthScore < EPWORTH_NOT_MEDICALLY_NECESSARY_THRESHOLD ? 'Flagged for Review' : 'Probably Eligible';
}
